<script setup lang="ts">
import type { Option } from '@autobid/strapi-integration/typescript/webapi/Dictionary'
import { useFormGroupItem } from '@autobid/nuxt-pinia-store/store/searchStore/useFormGroupItem'
import CheckboxLabel from '@autobid/ui/components/elements/checkbox/CheckboxLabel.vue'
import { Switch, TransitionRoot } from '@headlessui/vue'

interface Props {
  option: Option
  color?: string
  groupKey: string
}

const props = defineProps<Props>()

const checked = useFormGroupItem(props.groupKey, props.option.id)

function getIconColor(hexColor: string) {
  const rgb = hexColor.match(/[A-Z0-9]{2}/gi)?.map(val => Number.parseInt(val, 16))

  if (!rgb) {
    return 'text-black'
  }

  const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000

  return brightness > 128 ? 'text-black' : 'text-white'
}
</script>

<template>
  <Switch
    v-model="checked"
    class="relative z-[999] flex w-fit items-center gap-4 overflow-visible"
  >
    <div
      class="z-[9999] flex h-11 w-11 min-w-[2.75rem] items-center justify-center rounded-md border-2 border-primary transition hover:scale-110"
      :style="color ? `background-color: ${color}` : ''"
      :class="checked ? 'bg-primary' : 'bg-white'"
    >
      <TransitionRoot
        enter="transition-opacity duration-75"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity duration-150"
        leave-from="opacity-100"
        leave-to="opacity-0"
        :show="checked"
        appear
      >
        <Icon
          mode="svg"
          name="material-symbols:check"
          size="36"
          :class="getIconColor(color ?? '#fff')"
        />
      </TransitionRoot>
    </div>
    <CheckboxLabel
      :label-props="{
        class: 'text-ellipsis overflow-hidden',
        title: option.label,
      }"
    >
      {{ option.label }}
    </CheckboxLabel>
  </Switch>
</template>
